// ShipmentDetail — /receiving/shipments/:id. One delivery, three numbers per line.
//
// ── THE WHOLE PAGE IS ONE COMPARISON ────────────────────────────────────────
// Ordered · Shipped · Booked in. Every line shows all three, side by side, and
// where they disagree the disagreement is the point rather than an error.
//
//   ordered < shipped   they sent more than we asked for
//   shipped < ordered   a short shipment, and the order line stays open
//   booked < shipped    boxes still to come in, or boxes that never will
//
// That third row is the one this page exists for. Six weeks after a delivery,
// "we received 34 against your 40" is half an answer; the other half is WHICH
// boxes, with barcodes, so somebody can go and look at them. Hence the box list
// under each line.
//
// ── WHAT THIS PAGE IS NOT ───────────────────────────────────────────────────
// It has no actions. Nothing here receives, edits or deletes: a packing list is a
// record of what a supplier said, and a record you can edit is not evidence. Boxes
// are received from the Receiving Hub and money is chased from Returns, both linked
// rather than duplicated.

import React from 'react';
import { Link, Redirect, useParams } from 'react-router-dom';
import BackLink from '../components/BackLink';
import { Tag } from '../components/ui/Tag';
import FadeIn from '../components/ui/FadeIn';
import { Tooltip } from '../components/ui/Tooltip';
import { useToast } from '../components/Toast/ToastContext';
import ItemName from '../components/ItemName';
import useUserStore from '../stores/useUserStore';
import { formatCents } from '../Procurement/poApi';
import { formatEventDate } from '../utils/date-utils';
import PageLayout from '../components/ui/PageLayout';
import { Panel } from '../components/ui/Panel';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonGroup, SkeletonText } from '../components/ui/Skeleton';
import { usePackingListApi, type PackingListRecord, type PackingListRecordLine } from './packingListApi';

const SOURCE_LABELS: Record<string, string> = {
  packing_list_pdf: 'Read from a PDF',
  packing_list_photo: 'Read from photographs',
};

/**
 * One of the three numbers, with the treatment its disagreement earns.
 *
 * Amber ONLY where a person might have to do something. A shipped quantity above
 * what was ordered is worth a color; one below it is a short shipment, which the
 * receiving list is already carrying, and coloring it here would be a second
 * alarm for a thing already being chased.
 */
const Figure: React.FC<{ label: string; value: number | null; tone?: 'plain' | 'warn' | 'good' }> = ({
  label,
  value,
  tone = 'plain',
}) => (
  <div>
    <span className="block text-[10px] uppercase tracking-wider text-on-surface-variant">{label}</span>
    <span
      className={`text-sm tabular-nums ${
        tone === 'warn' ? 'text-amber-300' : tone === 'good' ? 'text-green-300' : 'text-on-surface'
      }`}
    >
      {value === null || value === undefined ? '—' : value}
    </span>
  </div>
);

const LineCard: React.FC<{ line: PackingListRecordLine; canSeeFinancials: boolean }> = ({
  line,
  canSeeFinancials,
}) => {
  const shortShipped =
    Number.isInteger(line.quantity_expected) && line.quantity_shipped < (line.quantity_expected as number);
  const overShipped =
    Number.isInteger(line.quantity_expected) && line.quantity_shipped > (line.quantity_expected as number);
  const stillOwed = line.quantity_boxed < line.quantity_shipped;

  return (
    // Tagged because the box rows below are `<li>` too, so a listitem query on
    // the outer list matches both levels.
    <li data-testid="shipment-line">
    <Panel label={`Slip line ${line.part_number_observed || 'not matched'}`} className="space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm text-on-surface">
            {line.sku ? (
              <ItemName size="sm" inline item={line} />
            ) : (
              <span className="text-on-surface-variant italic">not matched to an item</span>
            )}
          </p>
          <p className="text-[11px] text-on-surface-variant leading-snug">
            {/* What the SLIP printed, always kept: on an unmatched line it is the
                only identity the row has. */}
            Slip said <span className="font-mono text-on-surface-variant">{line.part_number_observed || '—'}</span>
            {line.description_observed && <> · {line.description_observed}</>}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {line.po_number && (
            <span className="font-mono text-[11px] text-on-surface-variant">{line.po_number}</span>
          )}
          {!line.purchase_order_line_id && (
            <Tooltip label="This row matched nothing we had ordered">
              <Tag size="sm">not ordered</Tag>
            </Tooltip>
          )}
        </div>
      </div>

      {/* THE COMPARISON. Three numbers, one row, no arithmetic asked of the reader. */}
      <div className="grid grid-cols-3 gap-3 max-w-md">
        <Figure label="Ordered" value={line.quantity_expected} />
        <Figure
          label="Shipped"
          value={line.quantity_shipped}
          tone={overShipped ? 'warn' : 'plain'}
        />
        <Figure
          label="Booked in"
          value={line.quantity_boxed}
          tone={stillOwed ? 'warn' : line.quantity_boxed > 0 ? 'good' : 'plain'}
        />
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
        {shortShipped && (
          <span className="text-on-surface-variant">
            Short by {(line.quantity_expected as number) - line.quantity_shipped} against the order.
          </span>
        )}
        {overShipped && (
          <span className="text-amber-300">
            {line.quantity_shipped - (line.quantity_expected as number)} more than the order asked for.
          </span>
        )}
        {stillOwed && (
          <span className="text-amber-300">
            {line.quantity_shipped - line.quantity_boxed} not booked in yet.
          </span>
        )}
        {line.lot_number && <span className="text-on-surface-variant">Lot {line.lot_number}</span>}
        {line.unit_price_cents_observed !== null && (
          <span className="text-on-surface-variant tabular-nums">
            Slip priced at {formatCents(line.unit_price_cents_observed)}
            {canSeeFinancials && Number.isInteger(line.ordered_unit_cost_cents) && (
              <> against {formatCents(line.ordered_unit_cost_cents as number)} ordered</>
            )}
          </span>
        )}
      </div>

      {/* ── the boxes, which is what somebody came here for ─────────────── */}
      {line.boxes.length > 0 ? (
        <ul className="space-y-1 border-t border-gray-700/40 pt-2">
          {line.boxes.map((box) => (
            <li key={box.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[11px]">
              <Link
                to={`/receiving/item/${box.id}`}
                className="font-mono text-primary hover:underline"
              >
                {box.barcode}
              </Link>
              <span className="text-on-surface tabular-nums">{box.quantity_received}</span>
              <span className="text-on-surface-variant">{box.location_name || 'unknown location'}</span>
              {box.disposition === 'rejected' && (
                <Tag size="sm" tone="warn">not acceptable</Tag>
              )}
              <span className="text-on-surface-variant ml-auto tabular-nums">
                {formatEventDate(String(box.received_at).slice(0, 10), 'MMM D')}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[11px] text-on-surface-variant border-t border-gray-700/40 pt-2">
          Nothing booked in against this line yet.
        </p>
      )}
    </Panel>
    </li>
  );
};

export const ShipmentDetail: React.FC = () => {
  const api = usePackingListApi();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const user = useUserStore((s) => s.user);
  const canSeeFinancials = !!user?.can_financial;

  const canAccess =
    user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing || user.can_procurement;

  const [record, setRecord] = React.useState<PackingListRecord | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await api.get(id);
        if (!cancelled) setRecord(data);
      } catch (err: any) {
        if (cancelled) return;
        if (err?.status === 404) setNotFound(true);
        else toast.error(err?.data?.error?.message || err?.message || 'Failed to load that shipment');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!canAccess) return <Redirect to="/" />;

  const totals = record
    ? record.lines.reduce(
      (acc, l) => ({
        shipped: acc.shipped + l.quantity_shipped,
        boxed: acc.boxed + l.quantity_boxed,
      }),
      { shipped: 0, boxed: 0 }
    )
    : { shipped: 0, boxed: 0 };

  const receivedBy = record
    ? [record.created_by_firstname, record.created_by_lastname].filter(Boolean).join(' ')
    : '';

  return (
    <PageLayout width="narrow" kind="record" className="pt-4" header={<BackLink to="/receiving/shipments" label="Shipments" className="mb-5" />}>
    <div className="space-y-5">
      {loading && (
        <SkeletonGroup label="Loading the shipment">
          <SkeletonText lines={3} />
        </SkeletonGroup>
      )}

      {/* NO DEAD END. A shipment that is not there keeps the back link above it, never a blank. */}
      {notFound && (
        <FadeIn>
          <EmptyState title="That shipment is not recorded here.">
            It may have been read on the other product, or the link may be old.
          </EmptyState>
        </FadeIn>
      )}

      {record && (
        <FadeIn className="space-y-5">
          <header className="space-y-2">
            <h1 className="text-2xl font-bold text-primary font-mono">
              {record.slip_number || 'Unnumbered slip'}
            </h1>
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-on-surface-variant">Supplier</dt>
                <dd className="text-on-surface">
                  {record.vendor_name || record.vendor_observed || '—'}
                </dd>
                {/* The two disagree more often than anyone expects: a division name,
                    a d/b/a, a freight forwarder. Both are kept, and the printed one
                    is shown when it differs. */}
                {record.vendor_name
                  && record.vendor_observed
                  && record.vendor_name !== record.vendor_observed && (
                  <dd className="text-[11px] text-on-surface-variant leading-snug">
                    Printed as {record.vendor_observed}
                  </dd>
                )}
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-on-surface-variant">Shipped</dt>
                <dd className="text-on-surface tabular-nums">
                  {record.ship_date ? formatEventDate(record.ship_date, 'MMM D, YYYY') : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-on-surface-variant">Units</dt>
                <dd className="text-on-surface tabular-nums">
                  {totals.boxed} of {totals.shipped} booked in
                </dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-wider text-on-surface-variant">Read</dt>
                <dd className="text-on-surface">
                  {formatEventDate(String(record.created_at).slice(0, 10), 'MMM D, YYYY')}
                </dd>
                <dd className="text-[11px] text-on-surface-variant leading-snug">
                  {receivedBy || 'by somebody since removed'}
                  {SOURCE_LABELS[record.source] ? ` · ${SOURCE_LABELS[record.source].toLowerCase()}` : ''}
                </dd>
              </div>
            </dl>
            {record.notes && (
              <p className="text-xs text-on-surface-variant leading-relaxed whitespace-pre-line">{record.notes}</p>
            )}
          </header>

          <section className="space-y-2">
            <h2 className="text-[11px] uppercase tracking-wider text-on-surface-variant font-semibold">
              What the slip said, and what turned up
            </h2>
            <ul className="space-y-2" data-testid="shipment-lines">
              {record.lines.map((line) => (
                <LineCard key={line.id} line={line} canSeeFinancials={canSeeFinancials} />
              ))}
            </ul>
          </section>

          {/* Where the two things a person might want to DO actually live. Linked
              rather than duplicated: this page is a record, not a workbench. */}
          <p className="text-xs text-on-surface-variant leading-relaxed">
            Boxes are booked in from{' '}
            <Link to="/receiving" className="text-primary hover:underline">
              the Receiving Hub
            </Link>
            . Credit for anything that was not acceptable is chased under{' '}
            <Link to="/procurement/returns" className="text-primary hover:underline">
              Returns
            </Link>
            .
          </p>
        </FadeIn>
      )}
    </div>
    </PageLayout>
  );
};

export default ShipmentDetail;
